import type { ApiErrorBody } from '@brimstone/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const LOGIN_REDIRECT_KEY = 'brimstone:loginRedirectAt';
/** A second 401 this soon after logging in means the session isn't sticking: stop looping. */
const LOGIN_LOOP_MS = 15_000;

function readTimestamp(): number {
  try {
    return Number(window.sessionStorage.getItem(LOGIN_REDIRECT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeTimestamp(value: number): void {
  try {
    window.sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(value));
  } catch {
    // Storage blocked: the loop guard is best effort.
  }
}

/** Full-page trip through /auth/login (and chestrose if needed), back to where we are. */
export function redirectToLogin(): void {
  const now = Date.now();
  if (now - readTimestamp() < LOGIN_LOOP_MS) {
    window.location.assign('/login?error=no_token');
    return;
  }
  writeTimestamp(now);
  const here = window.location.pathname + window.location.search;
  window.location.assign(`/auth/login?next=${encodeURIComponent(here)}`);
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  json?: unknown;
}

/** JSON request to the api. A 401 sends the browser to log in. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await fetch(path, {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    headers: options.json === undefined ? undefined : { 'content-type': 'application/json' },
    body: options.json === undefined ? undefined : JSON.stringify(options.json),
  });
  if (res.status === 401) {
    redirectToLogin();
    throw new ApiError(401, 'unauthenticated', 'Log in with Chestrose.');
  }
  const body = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const error = (body as Partial<ApiErrorBody> | null)?.error;
    throw new ApiError(res.status, error?.code ?? 'http_error', error?.message ?? res.statusText);
  }
  return body as T;
}
